import React, { useState, useEffect } from "react";
import { MessageCircle, X, Phone, MapPin } from "lucide-react";

const PHONE = "9779851423235"; // +977 9851423235 (KhojByte support)
const DEFAULT_MESSAGE = "Hello KhojByte! I'd like help with my research. Please share more details: https://khojbyte.com/get-help";

/**
 * Floating WhatsApp chat-head that links to wa.me/<phone>. Shows a
 * small expandable card with phone + address before deep-linking.
 */
export default function WhatsAppWidget() {
  const [open, setOpen] = useState(false);
  const [hasNudged, setHasNudged] = useState(false);

  useEffect(() => {
    // Auto-nudge once after 8s on first visit (sessionStorage)
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("kb_wa_nudged")) { setHasNudged(true); return; }
    const t = setTimeout(() => {
      setOpen(true); setHasNudged(true);
      sessionStorage.setItem("kb_wa_nudged", "1");
      // Auto-close after 6 s
      setTimeout(() => setOpen(false), 6000);
    }, 8000);
    return () => clearTimeout(t);
  }, []);

  const waHref = `https://wa.me/${PHONE}?text=${encodeURIComponent(DEFAULT_MESSAGE)}`;

  return (
    <>
      {/* Expanded card */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 w-72 bg-white border border-slate-200 rounded-sm shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
          data-testid="whatsapp-widget-card"
        >
          <div className="bg-[#25D366] px-4 py-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              </div>
              <div>
                <div className="text-sm font-medium leading-tight">KhojByte Support</div>
                <div className="kb-mono text-[10px] tracking-wider opacity-90">USUALLY REPLIES IN ~1H</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="whatsapp-widget-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-3 text-sm text-kb-ink">
            <p className="leading-relaxed">
              Have a research question? <strong>Chat with us on WhatsApp</strong> — we typically respond within an hour during working days.
            </p>
            <div className="space-y-1.5 text-xs text-kb-ink-muted">
              <div className="flex items-start gap-2"><Phone className="w-3.5 h-3.5 mt-0.5 text-[#25D366]" /><span>+977 9851423235</span></div>
              <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 text-[#25D366]" /><span>Tyanglaphat Chowk, Kirtipur, Kathmandu</span></div>
            </div>
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="block text-center bg-[#25D366] hover:bg-[#1faa53] text-white text-sm font-medium py-2.5 rounded-sm transition-colors"
              data-testid="whatsapp-widget-start-chat"
            >
              Start WhatsApp chat
            </a>
          </div>
        </div>
      )}

      {/* Floating chat head */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1faa53] shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105"
        title="Chat on WhatsApp"
        data-testid="whatsapp-widget-toggle"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="w-7 h-7 fill-current" aria-hidden="true">
          <path d="M16.001 3C9.373 3 4 8.373 4 15.001c0 2.4.722 4.633 1.96 6.51L4 29l7.704-1.918a11.95 11.95 0 0 0 4.297.804h.005C22.633 27.886 28 22.514 28 15.886S22.633 3.886 16.001 3zm0 22.014c-1.49 0-2.94-.4-4.197-1.155l-.3-.18-4.572 1.138 1.157-4.452-.196-.314a9.864 9.864 0 0 1-1.518-5.25c0-5.448 4.434-9.882 9.886-9.882 5.448 0 9.881 4.434 9.881 9.882-.001 5.452-4.434 9.213-9.882 10.213zm5.39-7.388c-.295-.148-1.748-.864-2.02-.964-.271-.099-.469-.148-.667.149-.197.296-.766.964-.94 1.162-.173.198-.345.222-.642.074-.296-.148-1.252-.461-2.385-1.473-.882-.788-1.477-1.762-1.65-2.058-.173-.296-.018-.456.13-.604.133-.133.296-.345.444-.518.148-.173.197-.296.296-.494.099-.198.05-.371-.025-.519-.074-.148-.667-1.607-.913-2.198-.241-.578-.487-.5-.667-.51l-.567-.01a1.092 1.092 0 0 0-.79.371c-.271.297-1.037 1.013-1.037 2.471 0 1.459 1.061 2.867 1.21 3.066.148.197 2.087 3.187 5.058 4.467.707.305 1.258.487 1.687.624.708.225 1.351.193 1.86.117.567-.084 1.748-.715 1.996-1.405.246-.69.246-1.282.173-1.405-.074-.124-.272-.198-.567-.346z"/>
        </svg>
      </button>
      {/* Visual nudge ring when first opened */}
      {!hasNudged && (
        <span className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full animate-ping bg-[#25D366]/40 pointer-events-none" aria-hidden="true" />
      )}
    </>
  );
}
